import { toJS } from 'mobx';

import {
    accountLockedErrorMessage,
    accountMigratedErrorMessage,
    genericErrorMessage,
    integerRegex,
    invalidSignInErrorMessage,
    OtpApiStatusKey,
    otpMappings,
    OtpMessageTranslationKey,
    posGenericErrorMessage,
    posNoTransactionMessage,
    signInErrorTransKey
} from '~constants/Constants';
import {
    accountLockoutErrorCode,
    accountMigratedErrorCode,
    isInvalidCredentialErrorCode
} from '~constants/SignInErrorCodes';
import { SignUpType } from '~constants/SignUpTypeEnum';
import {
    FieldValueType,
    IFieldsValidations,
    IFormConfig,
    IRequestFieldsAdditionalMappings,
    PrimitiveValueType
} from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { handlePlaceholderValues } from '~utils/FormatText';

import {
    IAddUserResponse,
    IApiError,
    IOtpApiResponse,
    IUpdateUserResponse,
    IUserApiResponse,
    IUserSignInApiResponse,
    PosFrontierProcessResultType
} from './RampPgApiSdk';

export interface IUserRequestData {
    [key: string]: PrimitiveValueType | undefined;
}

export type ISignInRequestData = { emailAddress: string; password: string } | { token: string };

const signInErrorTranslationKeys = [
    invalidSignInErrorMessage,
    signInErrorTransKey,
    accountLockedErrorMessage,
    accountMigratedErrorMessage
] as const;

export type SignInErrorTranslationKey = typeof signInErrorTranslationKeys[number];
export type GuestCheckoutError = typeof genericErrorMessage;

export interface IStoreData {
    [key: string]: PrimitiveValueType | undefined;
}

const mapPosFrontiersResult = ({ resultCode }: PosFrontierProcessResultType) => {
    if (resultCode === -4) {
        return posNoTransactionMessage;
    } else if ([0, 1, 2].includes(resultCode)) {
        return undefined;
    } else {
        return posGenericErrorMessage;
    }
};

const convertFieldType = (
    expectedType?: FieldValueType,
    originData: PrimitiveValueType = ''
): PrimitiveValueType | undefined => {
    const originType = typeof originData as FieldValueType;
    switch (expectedType) {
        case 'number':
            if (originType === 'boolean') {
                // Uses 1/0 for boolean values
                return originData ? 1 : 0;
            } else if (originType === 'string') {
                const strData = originData.toString();

                // HK needs a new rule for "sisterBrandPromotions"
                // ::smsPromotions::||::emailPromotions::||::postalPromotions::||::phonePromotions::
                if (strData.includes('||')) {
                    return convertFieldType(
                        'number',
                        strData
                            .split('||')
                            .reduce((acc, c) => acc || convertFieldType('boolean', c), false)
                    );
                }

                if (integerRegex.test(strData)) {
                    // Integer stored as string
                    return strData.length > 0 ? parseInt(strData, 10) : undefined;
                }

                return strData.toLowerCase() === 'true' ? 1 : 0;
            }

            return originData;

        case 'boolean':
            if (originType === 'string') {
                const strData = originData.toString();

                if (['false', '0'].includes(strData)) {
                    return false;
                }
            }

            return Boolean(originData);

        case 'string':
            return originData.toString();

        default:
            return originData;
    }
};

const getAdditionalMappingValue = (
    storeData: IStoreData,
    additionalMapping: PrimitiveValueType,
    expectedType?: FieldValueType
) => {
    if (['number', 'boolean'].includes(typeof additionalMapping)) {
        // Bypass converting fixed values
        return additionalMapping;
    } else {
        const parsedValue = handlePlaceholderValues(additionalMapping as string, storeData);

        return convertFieldType(expectedType, parsedValue);
    }
};

const getRequestData = (
    storeData: IStoreData,
    requestFields: string[],
    fieldsValidations: IFieldsValidations,
    fieldAdditionalMappings: IRequestFieldsAdditionalMappings
): IUserRequestData => {
    return requestFields.reduce((payload, field) => {
        const expectedType = fieldsValidations[field]?.type;
        const formValue = storeData[field];
        if (fieldAdditionalMappings[field]) {
            return {
                ...payload,
                [field]: getAdditionalMappingValue(
                    storeData,
                    fieldAdditionalMappings[field],
                    // Fixed values does not have type specification
                    expectedType
                )
            };
        } else if (formValue === undefined) {
            // Skip untouched field that is not mandatory
            return payload;
        } else {
            return {
                ...payload,
                // There will be type specifications for all form fields
                [field]: convertFieldType(expectedType, storeData[field])
            };
        }
    }, {} as IUserRequestData);
};

const handleInverseFields = (formValues: IStoreData, inverseFields: string[]) => {
    const data = toJS(formValues);
    inverseFields.forEach((field) => {
        if (field in data) {
            const fieldValue = data[field];
            if (typeof fieldValue === 'boolean' || fieldValue === 1 || fieldValue === 0) {
                data[field] = !fieldValue;
            }
        }
    });

    return data;
};

// excludes fields like emailPromotions and acceptLoyaltyTerms when no opt in
export const filterSignUpRequestFields = (
    signUpType: SignUpType,
    requestFields: string[],
    requestFieldsAdditionalMappings: IRequestFieldsAdditionalMappings,
    formData: IStoreData
): string[] => {
    const excludedFields = new Set();

    // For PUT requests (partial users / SignUpType.ONLINE) include emailPromotions even if "0"
    if (
        signUpType !== SignUpType.ONLINE &&
        !formData.emailPromotions &&
        !requestFieldsAdditionalMappings.emailPromotions
    ) {
        excludedFields.add('emailPromotions');
    }

    if (!formData.acceptLoyaltyTerms && !requestFieldsAdditionalMappings.acceptLoyaltyTerms) {
        excludedFields.add('acceptLoyaltyTerms').add('loyaltyActiveFlag').add('loyaltySource');
    }

    return requestFields.filter((field) => !excludedFields.has(field));
};

// TODO: APACFTRS1-770: is it good to access a repository from DataMapper
const formSignUpRequest = (formValues: IStoreData, signUpType: SignUpType) => {
    const { iamConfig, inverseFields } = diContainer.get(
        serviceNames.configRepository
    ) as ConfigRepository;
    const {
        postRequestFields,
        putRequestFields,
        identifiers,
        fieldsValidations,
        requestFieldsAdditionalMappings
    } = iamConfig as IFormConfig;

    const data = handleInverseFields(formValues, inverseFields);
    const requestFields = signUpType === SignUpType.PARTIAL ? postRequestFields : putRequestFields;
    const filteredRequestFields = filterSignUpRequestFields(
        signUpType,
        requestFields,
        requestFieldsAdditionalMappings,
        data
    );

    const requestData = getRequestData(
        data,
        filteredRequestFields,
        fieldsValidations,
        requestFieldsAdditionalMappings
    );

    identifiers?.forEach((identifier) => {
        const value = requestData[identifier];
        if (typeof value === 'string' && value.trim() === '') {
            delete requestData[identifier];
        }
    });

    return requestData;
};

// 1. if mobile number exists, show mobile number already registered error
// 2. if two matches, return both entries
const getUserApiResponseMapper = (isUpdate: boolean) => (
    data: IUserApiResponse
): IAddUserResponse | IUpdateUserResponse => {
    const { users, features } = data;
    let posFrontiersErrorKey: string | undefined;
    if (features?.posFrontiers) {
        posFrontiersErrorKey = mapPosFrontiersResult(features?.posFrontiers);
    }
    const posIKalarCallbackUrl = features?.posIKalar?.resultMessage.callback_url;
    if (isUpdate) {
        // For PUT/PATCH request which is considered as update, there won't be more than one record.
        return {
            user: users[0],
            posFrontiersErrorKey,
            posIKalarCallbackUrl
        } as IUpdateUserResponse;
    } else {
        // Even if two identifiers belongs to one user, there will still be two records.
        return { users, posFrontiersErrorKey, posIKalarCallbackUrl };
    }
};

const DataMappings = {
    addUser: {
        request(data: IStoreData): IUserRequestData {
            return formSignUpRequest(data, SignUpType.PARTIAL);
        },
        response: getUserApiResponseMapper(false)
    },
    updateUser: {
        request(data: IStoreData): IUserRequestData {
            return formSignUpRequest(data, SignUpType.ONLINE);
        },
        response: getUserApiResponseMapper(true)
    },
    patchPassword: {
        response: getUserApiResponseMapper(true)
    },
    fromSignInError: {
        response(data: IUserSignInApiResponse): SignInErrorTranslationKey {
            if (data?.errors?.length) {
                const isAccountMigrationError = data.errors.some(
                    (error) => accountMigratedErrorCode === error.key
                );

                if (isAccountMigrationError) {
                    return accountMigratedErrorMessage;
                }

                const isAccountLocked = data.errors.some(
                    (error) => accountLockoutErrorCode === error.key
                );

                if (isAccountLocked) {
                    return accountLockedErrorMessage;
                }

                const isCredentialError = data.errors.some((error) =>
                    isInvalidCredentialErrorCode(error.key)
                );

                return isCredentialError ? invalidSignInErrorMessage : signInErrorTransKey;
            }

            return signInErrorTransKey;
        }
    },

    fromGuestCheckoutError: {
        response(data: IUserSignInApiResponse): GuestCheckoutError {
            return genericErrorMessage;
        }
    },
    fromOtp: {
        response(response: IOtpApiResponse): OtpMessageTranslationKey {
            const statusKey = response?.mfaStatus as OtpApiStatusKey;

            return otpMappings[statusKey] as OtpMessageTranslationKey;
        }
    },
    fromOtpError: {
        response({ errors }: IApiError): OtpMessageTranslationKey {
            if (errors && errors.length) {
                const errorKey = errors[0].key as OtpApiStatusKey;

                const errorMessageTranslationKey = otpMappings[
                    errorKey
                ] as OtpMessageTranslationKey;

                return errorMessageTranslationKey || genericErrorMessage;
            }

            return genericErrorMessage;
        }
    }
};

export default DataMappings;
